import { Alert, AlertTitle, Typography } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DotsProgress } from "shared-ts-ui-progress";
import { MobileBootstrapSessionHandler } from "./MobileBootstrapSessionHandler";

/**
 * A route that exchanges the short‑lived token for cookies and redirects to home.
 *
 * Behavior:
 * reads `token` from query, `POST` to `/api/auth/exchange-token` with `credentials: 'include'`,
 * then navigates to `/`.
 * Shows minimal status text (pending/success/error)
 */

export const MobileBootstrapSession = () => {
  const { t } = useTranslation("shared-ts-ui-identity");
  const [status, setStatus] = useState<"pending" | "success" | "error">(
    "pending"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const token = params.get("token");

    console.log("[MobileBootstrapSession] Starting token exchange");
    console.log(
      "[MobileBootstrapSession] Token:",
      token ? "present" : "missing"
    );
    console.log("[MobileBootstrapSession] Full URL:", window.location.href);

    if (!token) {
      const err = "Missing token parameter";
      console.error("[MobileBootstrapSession] Error:", err);
      setErrorMessage(err);
      setStatus("error");
      new MobileBootstrapSessionHandler().onError(err);
      return;
    }

    (async () => {
      try {
        const exchangeUrl = `/api/auth/exchange-token?token=${encodeURIComponent(
          token
        )}`;
        console.log(
          "[MobileBootstrapSession] Calling exchange endpoint:",
          exchangeUrl
        );

        const res = await fetch(exchangeUrl, {
          method: "POST",
          credentials: "include",
        });

        console.log("[MobileBootstrapSession] Response status:", res.status);
        console.log(
          "[MobileBootstrapSession] Response header X-Stale-External-Session:",
          res.headers.get("X-Stale-External-Session")
        );
        console.log("[MobileBootstrapSession] Response OK:", res.ok);

        if (!res.ok) {
          const responseText = await res.text();
          console.error(
            "[MobileBootstrapSession] Response body:",
            responseText
          );
          throw new Error(
            `Token exchange failed: ${res.status} ${res.statusText}`
          );
        }

        console.log("[MobileBootstrapSession] Token exchange successful");
        setStatus("success");

        // After successful token exchange, reload to trigger authentication check
        // This will cause LoginProvider to re-check auth and load the user
        console.log("[MobileBootstrapSession] Calling onSuccess callback");
        new MobileBootstrapSessionHandler().onSuccess();
      } catch (e) {
        const err =
          (e as Error).message ?? "Unknown error during token exchange";
        console.error(
          "[MobileBootstrapSession] Error during token exchange:",
          err
        );
        setErrorMessage(err);
        setStatus("error");
        new MobileBootstrapSessionHandler().onError(err);
      }
    })();
  }, []);

  return (
    <div style={{ padding: 16, textAlign: "center" }}>
      {status === "pending" && (
        <Typography>
          {t("Signing you in")}
          <DotsProgress />
        </Typography>
      )}
      {status === "success" && (
        <Typography>
          {t("Signed in successfully, redirecting")}
          <DotsProgress />
        </Typography>
      )}
      {status === "error" && (
        <Alert>
          <AlertTitle>{t("Failed to sign in")}</AlertTitle>
          {errorMessage}
        </Alert>
      )}
    </div>
  );
};
